import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar as CalendarIcon, Plus, Clock } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function AppointmentsPage() {
  const { role } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]); // Patients for the form (filtered by doctor for nurses)
  const [allPatients, setAllPatients] = useState([]); // All patients for displaying names in list
  const [myDoctors, setMyDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    doctor_id: '',
    appointment_date: '',
    appointment_time: '',
    reason: '',
    status: 'pendiente'
  });
  const isNurse = role === 'nurse';

  useEffect(() => {
    if (isNurse) {
      fetchMyDoctors();
      fetchAllPatients(); // Load all patients for displaying names in appointments list
    } else {
      fetchPatients();
    }
    fetchAppointments();
  }, [isNurse]);

  // Fetch all patients (for nurses to display patient names in appointments list)
  const fetchAllPatients = async () => {
    try {
      const response = await api.get('/patients/all');
      setAllPatients(response.data);
    } catch (error) {
      console.error('Error fetching all patients:', error);
    }
  };

  const fetchMyDoctors = async () => {
    try {
      const response = await api.get('/nurses/my-doctors');
      setMyDoctors(response.data);
      // If nurse has doctors, fetch patients from their first doctor's patients
      if (response.data && response.data.length > 0) {
        // Set default doctor - use user_id (not doctor_user_id)
        const firstDoctor = response.data[0];
        setFormData(prev => ({ ...prev, doctor_id: firstDoctor.user_id }));
        fetchPatientsForDoctor(firstDoctor.user_id);
      }
    } catch (error) {
      toast.error('Error al cargar médicos asignados');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientsForDoctor = async (doctorId) => {
    if (!doctorId) {
      setPatients([]);
      return;
    }
    try {
      const response = await api.get(`/patients?doctor_id=${doctorId}`);
      setPatients(response.data);
    } catch (error) {
      toast.error('Error al cargar pacientes');
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data);
    } catch (error) {
      toast.error('Error al cargar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleDoctorChange = (doctorId) => {
    setFormData({ ...formData, doctor_id: doctorId, patient_id: '' });
    fetchPatientsForDoctor(doctorId);
  };

  const fetchAppointments = async () => {
    try {
      if (isNurse) {
        // Nurses get all appointments (general endpoint handles this)
        const response = await api.get('/appointments');
        setAppointments(response.data);
      } else {
        // Doctors get their own appointments
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userData = JSON.parse(userStr);
          const response = await api.get(`/appointments/doctor/${userData.id}`);
          setAppointments(response.data);
        }
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Combine date and time with Peru timezone (UTC-5)
      // Create the datetime string with timezone offset to preserve the local time
      const dateTime = `${formData.appointment_date}T${formData.appointment_time}:00-05:00`;

      const appointmentData = {
        patient_id: formData.patient_id,
        doctor_id: isNurse ? formData.doctor_id : '', // Nurses must specify doctor
        appointment_date: dateTime,
        reason: formData.reason,
        status: formData.status
      };

      await api.post('/appointments', appointmentData);
      toast.success('Cita agendada exitosamente');
      setDialogOpen(false);
      // Reset form (keep doctor_id if nurse)
      setFormData(prev => ({
        patient_id: '',
        doctor_id: isNurse ? prev.doctor_id : '',
        appointment_date: '',
        appointment_time: '',
        reason: '',
        status: 'pendiente'
      }));
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agendar cita');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="appointments-title">
            Gestión de Citas
          </h1>
          <p className="text-muted-foreground mt-2">
            Administra las citas médicas de tus pacientes
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary" data-testid="create-appointment-button">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Cita
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="create-appointment-dialog">
            <DialogHeader>
              <DialogTitle>Agendar Nueva Cita</DialogTitle>
              <DialogDescription>
                Complete los datos de la cita médica
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Selección de Médico (solo para enfermeras) */}
              {isNurse && (
                <div className="space-y-2">
                  <Label htmlFor="doctor_id">Médico *</Label>
                  <Select value={formData.doctor_id} onValueChange={handleDoctorChange} required>
                    <SelectTrigger data-testid="doctor-select">
                      <SelectValue placeholder="Seleccionar médico..." />
                    </SelectTrigger>
                    <SelectContent>
                      {myDoctors.map((doctor) => (
                        <SelectItem key={doctor.user_id} value={doctor.user_id}>
                          Dr(a). {doctor.full_name} - {doctor.specialization || 'General'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Selección de Paciente */}
              <div className="space-y-2">
                <Label htmlFor="patient_id">Paciente *</Label>
                <Select value={formData.patient_id} onValueChange={(value) => setFormData({ ...formData, patient_id: value })} required>
                  <SelectTrigger data-testid="patient-select">
                    <SelectValue placeholder="Seleccionar paciente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((patient) => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.first_name} {patient.last_name} - DNI: {patient.dni}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Fecha y Hora */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="appointment_date">Fecha *</Label>
                  <Input
                    id="appointment_date"
                    type="date"
                    value={formData.appointment_date}
                    onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                    required
                    data-testid="appointment-date-input"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appointment_time">Hora *</Label>
                  <Input
                    id="appointment_time"
                    type="time"
                    value={formData.appointment_time}
                    onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
                    required
                    data-testid="appointment-time-input"
                  />
                </div>
              </div>

              {/* Motivo */}
              <div className="space-y-2">
                <Label htmlFor="reason">Motivo de la Cita</Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Describa el motivo de la cita..."
                  data-testid="reason-input"
                  rows={3}
                />
              </div>

              {/* Estado */}
              <div className="space-y-2">
                <Label htmlFor="status">Estado</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="confirmada">Confirmada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-appointment-button">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-primary" data-testid="submit-appointment-button">
                  <Clock className="w-4 h-4 mr-2" />
                  Agendar Cita
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 col-span-2" data-testid="calendar-card">
          <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Calendario de Citas
          </h2>
          <div className="flex flex-col items-center justify-center py-12">
            <CalendarIcon className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              Calendario interactivo próximamente
            </p>
            <p className="text-sm text-muted-foreground text-center mt-2">
              Por ahora, puedes crear citas usando el botón "Nueva Cita"
            </p>
          </div>
        </Card>

        <Card className="p-6" data-testid="upcoming-appointments-card">
          <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Próximas Citas
          </h2>
          <div className="space-y-3">
            {appointments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No hay citas programadas
              </p>
            ) : (
              appointments.slice(0, 5).map((apt) => {
                // For nurses, search in allPatients; for doctors, search in patients
                const patientList = isNurse ? allPatients : patients;
                const patient = patientList.find(p => p.id === apt.patient_id);
                const appointmentDate = new Date(apt.appointment_date);
                return (
                  <div key={apt.id} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                    <p className="font-medium text-sm">
                      {patient ? `${patient.first_name} ${patient.last_name}` : 'Paciente'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {appointmentDate.toLocaleDateString('es-PE')} - {appointmentDate.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {apt.reason && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {apt.reason}
                      </p>
                    )}
                    <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${
                      apt.status === 'confirmada' ? 'bg-green-100 text-green-800' :
                      apt.status === 'pendiente' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {apt.status.charAt(0).toUpperCase() + apt.status.slice(1)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
